import {
    EnvelopeIcon,
    MapPinIcon,
    PencilIcon,
    TrashIcon,
    UserIcon,
    UserPlusIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';

import axios from 'axios';

import { Badge } from '@/components/core/Badge';
import { Button } from '@/components/core/Button';
import { Checkbox } from '@/components/core/Checkbox';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Icon } from '@/components/core/Icon';
import { IconButton } from '@/components/core/IconButton';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { Tabs, TabsList, TabsTrigger } from '@/components/core/Tabs';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';

import { contactErrorMessage, formatContactDate } from './businessPartner';
import { setLocationHash } from './contactsTab';
import { DeleteInvitationDialog } from './DeleteInvitationDialog';
import { EditInvitationDialog } from './EditInvitationDialog';
import {
    canEditInvitation,
    canResendInvitation,
    ContactRequestRow,
    EmailInvitationRecord,
    filterContactRequests,
    InvitationContactRecord,
    InvitationQuery,
    InvitationRequestType,
    InvitationSubTab,
    invitationSubTab,
    invitationTypeLabelKey,
    statusBadgeVariant,
    statusTitleKey,
} from './invitation';
import {
    useContactRequests,
    usePendingInvitations,
    useProcessInvitation,
    useSaveEmailInvitation,
} from './invitation.queries';
import { SendEmailInvitationDialog } from './SendEmailInvitationDialog';

const currentHash = (): string => {
    try {
        return String(getInjector().get('$location').hash() ?? '');
    } catch {
        return window.location.hash.replace(/^#/, '');
    }
};

const Avatar = ({ imageUrl, alt, email }: { imageUrl?: string; alt?: string; email?: boolean }) => (
    <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-full tw-bg-fill-secondary">
        {email ? (
            <Icon icon={EnvelopeIcon} className="tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6" variant="secondary" />
        ) : imageUrl ? (
            <img src={imageUrl} alt={alt ?? ''} className="tw-h-full tw-w-full tw-object-cover" />
        ) : (
            <Icon icon={UserIcon} className="tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6" variant="secondary" />
        )}
    </div>
);

const Labeled = ({ label, value, always }: { label: string; value?: string; always?: boolean }) => {
    if (!always && !value) {
        return null;
    }
    return (
        <p className="tw-break-words tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
            <span className="tw-text-t-secondary">{label}:</span> {value}
        </p>
    );
};

const EmptyInvitations = ({ message }: { message: string }) => (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-py-10">
        <span className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-bg-fill-secondary lg:tw-h-20 lg:tw-w-20">
            <Icon icon={UserIcon} className="tw-h-8 tw-w-8 lg:tw-h-10 lg:tw-w-10" variant="dark" />
        </span>
        <h2 className="tw-text-center tw-text-t-base tw-font-bold tw-text-h-primary lg:tw-text-t-lg">{message}</h2>
    </div>
);

interface PendingListProps {
    rows: InvitationContactRecord[];
    loading: boolean;
    onAccept: (organizationId: number) => void;
    onReject: (organizationId: number) => void;
    busyId?: number;
}

const PendingList = ({ rows, loading, onAccept, onReject, busyId }: PendingListProps) => {
    const { t } = useTranslation();
    return (
        <div id="pendingInvitations">
            <div id="results-table">
                {loading && <Loader />}
                {!loading && rows.length === 0 && <EmptyInvitations message={t('NO_MATCHES')} />}
                {rows.map((organization, index) => (
                    <div key={organization.organizationId ?? organization.id ?? index}>
                        {index > 0 && <div className="tw-h-px tw-w-full tw-bg-gray-200" />}
                        <div className="invitation invitation-row tw-flex tw-flex-col tw-gap-4 tw-p-3 lg:tw-flex-row lg:tw-items-center">
                            <Avatar imageUrl={organization.imageUrl} alt={organization.name} />
                            <div className="tw-min-w-0 tw-flex-1">
                                <div className="tw-break-words tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                                    {organization.name}
                                </div>
                                {organization.gln && (
                                    <div className="tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">{organization.gln}</div>
                                )}
                                {organization.address && (
                                    <div className="tw-mt-1 tw-flex tw-items-start tw-gap-2 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                        <Icon icon={MapPinIcon} className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 lg:tw-h-5 lg:tw-w-5" variant="secondary" />
                                        <span className="tw-break-words">{organization.address}</span>
                                    </div>
                                )}
                                <div className="tw-mt-1 tw-flex tw-items-start tw-gap-2 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                    <Icon icon={EnvelopeIcon} className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 lg:tw-h-5 lg:tw-w-5" variant="secondary" />
                                    <span className="tw-break-all">{organization.email}</span>
                                </div>
                            </div>
                            <div className="tw-min-w-0 tw-flex-1">
                                <Labeled
                                    label={t('REASON_FOR_INVITE')}
                                    value={t(invitationTypeLabelKey(organization.invitationType) ?? '')}
                                    always
                                />
                                <Labeled label={t('MESSAGING.MESSAGE')} value={organization.text} always />
                            </div>
                            <div className="tw-flex tw-shrink-0 tw-flex-col tw-items-stretch tw-gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={organization.organizationId == null || busyId === organization.organizationId}
                                    onClick={() => organization.organizationId != null && onReject(organization.organizationId)}
                                >
                                    {t('REJECT_INVITIATION')}
                                </Button>
                                <Button
                                    id="accept-invitation"
                                    type="button"
                                    disabled={organization.organizationId == null || busyId === organization.organizationId}
                                    onClick={() => organization.organizationId != null && onAccept(organization.organizationId)}
                                >
                                    {t('ACCEPT_INVITIATION')}
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const StatusBadge = ({ state }: { state?: string }) => {
    const { t } = useTranslation();
    const titleKey = statusTitleKey(state);
    if (!state) {
        return null;
    }
    return (
        <span title={titleKey ? t(titleKey) : undefined}>
            <Badge variant={statusBadgeVariant(state)}>{state}</Badge>
        </span>
    );
};

interface RequestListProps {
    rows: ContactRequestRow[];
    loading: boolean;
    onResend: (row: ContactRequestRow) => void;
    onEditEmail: (row: ContactRequestRow) => void;
    onEditContact: (row: ContactRequestRow) => void;
    onDelete: (row: ContactRequestRow) => void;
    resendingId?: number;
}

const RequestList = ({
    rows,
    loading,
    onResend,
    onEditEmail,
    onEditContact,
    onDelete,
    resendingId,
}: RequestListProps) => {
    const { t } = useTranslation();
    return (
        <div id="contactRequests">
            {loading && <Loader />}
            {!loading && rows.length === 0 && <EmptyInvitations message={t('NO_MATCHES')} />}
            {rows.map((row, index) => {
                const email = row.requestType === 'EMAIL_INVITATION';
                const originalId = row.original.id;
                return (
                    <div key={`${row.requestType}-${originalId ?? index}`}>
                        {index > 0 && <div className="tw-h-px tw-w-full tw-bg-gray-200" />}
                        <div className="invitation tw-flex tw-flex-col tw-gap-4 tw-p-3 lg:tw-flex-row lg:tw-items-center">
                            <div className="tw-flex tw-w-28 tw-shrink-0 tw-flex-col tw-items-start tw-gap-2">
                                <Avatar imageUrl={row.imageUrl} alt={row.name} email={email} />
                                <StatusBadge state={row.invitationState} />
                            </div>
                            <div className="tw-min-w-0 tw-flex-1">
                                {(row.name || (email && row.email)) && (
                                    <div className="tw-break-words tw-text-t-base tw-font-bold tw-text-t-primary lg:tw-text-t-lg">
                                        {row.name || row.email}
                                    </div>
                                )}
                                {row.address && (
                                    <div className="tw-mt-1 tw-flex tw-items-start tw-gap-2 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base">
                                        <Icon icon={MapPinIcon} className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 lg:tw-h-5 lg:tw-w-5" variant="secondary" />
                                        <span className="tw-break-words">{row.address}</span>
                                    </div>
                                )}
                                {!(email && !row.name) && <Labeled label={t('EMAIL')} value={row.email} always />}
                                {row.user && (
                                    <p className="tw-break-words tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                                        <span className="tw-text-t-secondary">{t('INVITATION.INVITED_BY')}</span> {row.user}
                                    </p>
                                )}
                                <Labeled
                                    label={t('INVITATION.INVITED_ON')}
                                    value={formatContactDate(row.date, true)}
                                    always
                                />
                                <Labeled
                                    label={t('INVITATION.ACCEPTED_ON')}
                                    value={formatContactDate(row.acceptanceDate, true)}
                                />
                            </div>
                            <div className="tw-min-w-0 tw-flex-1">
                                <Labeled
                                    label={t('INVITATION.INVITATION_TYPE')}
                                    value={row.invitationType ? t(row.invitationType) : ''}
                                    always
                                />
                                <Labeled label={t('REASON_FOR_INVITE')} value={row.invitationText} always />
                            </div>
                            <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-end">
                                {email ? (
                                    <>
                                        {canResendInvitation(row.invitationState) && (
                                            <IconButton
                                                icon={UserPlusIcon}
                                                size="md"
                                                className="tw-icon-primary"
                                                disabled={resendingId === originalId}
                                                title={t('INVITATION.RESEND_INVITATION.LABEL')}
                                                aria-label={t('INVITATION.RESEND_INVITATION.LABEL')}
                                                onClick={() => onResend(row)}
                                            />
                                        )}
                                        {canEditInvitation(row.invitationState) && (
                                            <IconButton
                                                icon={PencilIcon}
                                                size="md"
                                                className="tw-icon-primary"
                                                title={t('INVITATION.EDIT_INVITATION')}
                                                aria-label={t('INVITATION.EDIT_INVITATION')}
                                                onClick={() => onEditEmail(row)}
                                            />
                                        )}
                                        <IconButton
                                            icon={TrashIcon}
                                            size="md"
                                            className="tw-icon-primary"
                                            title={t('INVITATION.DELETE_INVITATION.LABEL')}
                                            aria-label={t('INVITATION.DELETE_INVITATION.LABEL')}
                                            onClick={() => onDelete(row)}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <IconButton
                                            icon={EnvelopeIcon}
                                            size="md"
                                            className="tw-icon-primary"
                                            title={t('CONTACTS.SEND_EMAIL')}
                                            aria-label={t('CONTACTS.SEND_EMAIL')}
                                            onClick={() => {
                                                window.location.href = `mailto:${row.email ?? ''}`;
                                            }}
                                        />
                                        {canEditInvitation(row.invitationState) && (
                                            <IconButton
                                                icon={PencilIcon}
                                                size="md"
                                                className="tw-icon-primary"
                                                title={t('INVITATION.EDIT_INVITATION.LABEL')}
                                                aria-label={t('INVITATION.EDIT_INVITATION.LABEL')}
                                                onClick={() => onEditContact(row)}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const Invitations = () => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { organization } = useOrganization();
    const communityManager = Boolean(organization?.communityManager);
    const [tab, setTab] = useState<InvitationSubTab>(() => invitationSubTab(currentHash()));
    const [query, setQuery] = useState<InvitationQuery>({ keyword: '', type: '', closedVisible: false });
    const [sendOpen, setSendOpen] = useState(false);
    const [editEmail, setEditEmail] = useState<EmailInvitationRecord | undefined>();
    const [editContact, setEditContact] = useState<InvitationContactRecord | undefined>();
    const [deleteEmail, setDeleteEmail] = useState<EmailInvitationRecord | undefined>();
    const [decision, setDecision] = useState<{
        organizationId: number;
        action: 'accept' | 'reject';
        name?: string;
    } | undefined>();
    const [busyId, setBusyId] = useState<number | undefined>();
    const pending = usePendingInvitations();
    const requests = useContactRequests();
    const processInvitation = useProcessInvitation();
    const resendInvitation = useSaveEmailInvitation();
    const filtered = useMemo(
        () => filterContactRequests(requests.data ?? [], query),
        [requests.data, query],
    );

    useEffect(() => {
        const sync = () => setTab(invitationSubTab(currentHash()));
        window.addEventListener('hashchange', sync);
        let unlisten: (() => void) | undefined;
        try {
            unlisten = getRootScope(getInjector()).$on('$locationChangeSuccess', sync);
        } catch {
            // The Angular shell is absent in tests.
        }
        return () => {
            window.removeEventListener('hashchange', sync);
            unlisten?.();
        };
    }, []);

    const chooseTab = (value: string) => {
        const next: InvitationSubTab = value === 'requested' ? 'requested' : 'pending';
        setTab(next);
        setLocationHash(next);
    };

    const ask = (organizationId: number, action: 'accept' | 'reject') => {
        const name = (pending.data ?? []).find((row) => row.organizationId === organizationId)?.name;
        setDecision({ organizationId, action, name });
    };

    const confirmDecision = () => {
        if (!decision) {
            return;
        }
        const { organizationId, action } = decision;
        setBusyId(organizationId);
        processInvitation.mutate({ organizationId, action }, {
            onSuccess: () => {
                toast.success(action === 'accept' ? 'INVITATION_ACCEPTED' : 'INVITATION_REJECTED');
                setDecision(undefined);
            },
            onError: (error) => {
                const data = axios.isAxiosError(error) ? error.response?.data : undefined;
                toast.error(contactErrorMessage(data, t, 'SAVE_ERROR_MESSAGE') ?? t('SAVE_ERROR_MESSAGE'));
            },
            onSettled: () => setBusyId(undefined),
        });
    };

    const resend = (row: ContactRequestRow) => {
        const original = row.original as EmailInvitationRecord;
        resendInvitation.mutate(original, {
            onSuccess: () => toast.success('INVITATION_UPDATED'),
            onError: () => toast.error('SAVE_ERROR_MESSAGE'),
        });
    };

    return (
        <div id="invitations" className="tw-flex tw-flex-col tw-gap-4">
            <Tabs value={tab} onValueChange={chooseTab}>
                <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-center lg:tw-justify-between">
                    <TabsList id="invitationsTab" className="tw-h-auto tw-bg-fill-secondary tw-p-1">
                        <TabsTrigger value="pending">{t('PENDING_INVITATIONS')}</TabsTrigger>
                        <TabsTrigger value="requested">{t('CONTACT_REQUESTS')}</TabsTrigger>
                    </TabsList>
                    {communityManager && (
                        <Button id="create-email-invitation" type="button" onClick={() => setSendOpen(true)}>
                            {t('SEND_EMAIL_INVITATION')}
                        </Button>
                    )}
                </div>
            </Tabs>

            {tab === 'pending' && (
                <PendingList
                    rows={pending.data ?? []}
                    loading={pending.isLoading}
                    busyId={busyId}
                    onAccept={(organizationId) => ask(organizationId, 'accept')}
                    onReject={(organizationId) => ask(organizationId, 'reject')}
                />
            )}

            {tab === 'requested' && (
                <div className="tw-flex tw-flex-col tw-gap-4">
                    <div className="tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row lg:tw-items-start">
                        <div className="tw-flex tw-w-full tw-flex-col tw-gap-2 lg:tw-w-64">
                            <Select
                                value={query.type || 'ALL'}
                                onValueChange={(value) => setQuery((current) => ({
                                    ...current,
                                    type: (value === 'ALL' ? '' : value) as InvitationRequestType,
                                }))}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="ALL">{t('INVITATIONS.ALL_INVITATIONS')}</SelectItem>
                                    <SelectItem value="CONTACT_REQUEST">{t('INVITATIONS.IN_PLATFORM')}</SelectItem>
                                    <SelectItem value="EMAIL_INVITATION">{t('INVITATIONS.EMAIL_INVITATIONS')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <Checkbox
                                    id="show-closed-invitations"
                                    isChecked={query.closedVisible}
                                    onCheckedChange={(checked) => setQuery((current) => ({
                                        ...current,
                                        closedVisible: checked === true,
                                    }))}
                                />
                                <label
                                    htmlFor="show-closed-invitations"
                                    className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base"
                                >
                                    {t('INVITATION.SHOW_CLOSED.LABEL')}
                                </label>
                            </div>
                        </div>
                        <Input
                            className="tw-w-full lg:tw-max-w-md"
                            aria-label={t('SEARCH')}
                            value={query.keyword}
                            onChange={(event) => setQuery((current) => ({ ...current, keyword: event.target.value }))}
                        />
                        <p className="tw-text-t-sm tw-text-t-secondary lg:tw-ml-auto lg:tw-pt-2 lg:tw-text-t-base">
                            {t('INVITATION.FOUND_MATCHES', { count: filtered.length })}
                        </p>
                    </div>
                    <RequestList
                        rows={filtered}
                        loading={requests.isLoading}
                        resendingId={resendInvitation.isPending ? resendInvitation.variables?.id : undefined}
                        onResend={resend}
                        onEditEmail={(row) => setEditEmail(row.original as EmailInvitationRecord)}
                        onEditContact={(row) => setEditContact(row.original as InvitationContactRecord)}
                        onDelete={(row) => setDeleteEmail(row.original as EmailInvitationRecord)}
                    />
                </div>
            )}

            <SendEmailInvitationDialog open={sendOpen} onClose={() => setSendOpen(false)} />
            <EditInvitationDialog
                open={editEmail != null}
                mode="email"
                email={editEmail}
                onClose={() => setEditEmail(undefined)}
            />
            <EditInvitationDialog
                open={editContact != null}
                mode="contact"
                contact={editContact}
                onClose={() => setEditContact(undefined)}
            />
            <DeleteInvitationDialog
                open={deleteEmail != null}
                invitation={deleteEmail}
                onClose={() => setDeleteEmail(undefined)}
            />
            <Dialog
                open={decision != null}
                onOpenChange={(open) => {
                    if (!open && !processInvitation.isPending) {
                        setDecision(undefined);
                    }
                }}
            >
                <DialogContent variant="small">
                    <DialogHeader>
                        <DialogHeaderDefault closeLabel={t('CLOSE')}>
                            {t('MODAL.CONFIRM_HEADER')}
                        </DialogHeaderDefault>
                    </DialogHeader>
                    <DialogBody>
                        <p className="tw-text-t-sm tw-text-t-primary lg:tw-text-t-base">
                            {t(decision?.action === 'reject' ? 'REJECT_INVITIATION' : 'ACCEPT_INVITIATION')}
                        </p>
                        {decision?.name && (
                            <p className="tw-mt-2 tw-text-t-sm tw-font-bold tw-text-t-primary lg:tw-text-t-base">
                                {decision.name}
                            </p>
                        )}
                    </DialogBody>
                    <DialogFooter>
                        <Button
                            type="button"
                            loading={processInvitation.isPending}
                            onClick={confirmDecision}
                        >
                            {t(decision?.action === 'reject' ? 'REJECT_INVITIATION' : 'ACCEPT_INVITIATION')}
                        </Button>
                        <Button
                            type="button"
                            variant="secondary"
                            disabled={processInvitation.isPending}
                            onClick={() => setDecision(undefined)}
                        >
                            {t('NO')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export { Invitations };
